package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Competence
import com.tedd.db.Competence_criterion
import com.tedd.db.Competence_family
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Recueil de compétences : le référentiel Famille → Compétence → Critères.
 * Un seul dépôt pour les trois tables, parce que l'interface les manipule ensemble
 * et que la suppression en cascade doit tenir dans une seule transaction.
 *
 * Cascade = soft-delete de chaque ligne avec son propre updated_at : un tombstone
 * LWW par ligne, propagé tel quel par la sync.
 */
class RecueilRepository(private val db: TeddDatabase) {

    fun observeFamilies(): Flow<List<Competence_family>> =
        db.competenceFamilyQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeCompetences(): Flow<List<Competence>> =
        db.competenceQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeCriteria(): Flow<List<Competence_criterion>> =
        db.competenceCriterionQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    // ── Familles ────────────────────────────────────────────────────────────

    suspend fun createFamily(
        name: String,
        description: String? = null,
        color: String = "#22508A",
        icon: String = "book",
        id: String = newId("cf"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.competenceFamilyQueries.selectAll().executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        db.competenceFamilyQueries.insert(id, name.trim(), description.clean(), color, icon, next, now, now)
        id
    }

    suspend fun updateFamily(id: String, name: String, description: String?, color: String, icon: String, orderIndex: Long) =
        withContext(Dispatchers.Default) {
            db.competenceFamilyQueries.update(name.trim(), description.clean(), color, icon, orderIndex, nowMillis(), id)
        }

    suspend fun reorderFamilies(orderedIds: List<String>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            orderedIds.forEachIndexed { i, id -> db.competenceFamilyQueries.reorder(i.toLong(), now, id) }
        }
    }

    suspend fun deleteFamily(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.competenceQueries.selectByFamily(id).executeAsList().forEach { deleteCompetenceCascade(it.id, now) }
            db.competenceFamilyQueries.softDelete(now, now, id)
        }
    }

    // ── Compétences ─────────────────────────────────────────────────────────

    suspend fun createCompetence(
        familyId: String,
        name: String,
        description: String? = null,
        skillId: String? = null,
        id: String = newId("cp"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.competenceQueries.selectByFamily(familyId).executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        db.competenceQueries.insert(id, familyId, skillId.clean(), name.trim(), description.clean(), next, now, now)
        id
    }

    suspend fun updateCompetence(
        id: String,
        familyId: String,
        name: String,
        description: String?,
        skillId: String?,
        orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.competenceQueries.update(familyId, skillId.clean(), name.trim(), description.clean(), orderIndex, nowMillis(), id)
    }

    /** Délie la compétence d'une skill Focus (par exemple si celle-ci a disparu). */
    suspend fun unlinkSkill(id: String) = withContext(Dispatchers.Default) {
        db.competenceQueries.selectById(id).executeAsOneOrNull()?.let {
            db.competenceQueries.update(it.family_id, null, it.name, it.description, it.order_index, nowMillis(), id)
        }
    }

    suspend fun reorderCompetences(orderedIds: List<String>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            orderedIds.forEachIndexed { i, id -> db.competenceQueries.reorder(i.toLong(), now, id) }
        }
    }

    suspend fun deleteCompetence(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction { deleteCompetenceCascade(id, now) }
    }

    /** À appeler dans une transaction : critères, liaisons de portfolio, puis la compétence. */
    private fun deleteCompetenceCascade(id: String, now: Long) {
        db.competenceCriterionQueries.selectByCompetence(id).executeAsList()
            .forEach { db.competenceCriterionQueries.softDelete(now, now, it.id) }
        db.portfolioPieceCompetenceQueries.selectByCompetence(id).executeAsList()
            .forEach { db.portfolioPieceCompetenceQueries.softDelete(now, now, it.id) }
        db.competenceQueries.softDelete(now, now, id)
    }

    // ── Critères ────────────────────────────────────────────────────────────

    suspend fun createCriterion(
        competenceId: String,
        label: String,
        description: String? = null,
        level: Int = 0,
        id: String = newId("cc"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val next = (db.competenceCriterionQueries.selectByCompetence(competenceId).executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
        val evaluatedAt = if (level > 0) now else null
        db.competenceCriterionQueries.insert(
            id, competenceId, label.trim(), description.clean(), level.toLong().coerceIn(0L, 3L), evaluatedAt, null, next, now, now,
        )
        id
    }

    suspend fun updateCriterion(id: String, label: String, description: String?) = withContext(Dispatchers.Default) {
        db.competenceCriterionQueries.selectById(id).executeAsOneOrNull()?.let {
            db.competenceCriterionQueries.update(
                it.competence_id, label.trim(), description.clean(), it.level, it.evaluated_at, it.evaluation_note,
                it.order_index, nowMillis(), id,
            )
        }
    }

    /** Le geste le plus fréquent : poser un niveau. Date d'évaluation = maintenant. */
    suspend fun setLevel(id: String, level: Int, note: String? = null) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.competenceCriterionQueries.setLevel(level.toLong().coerceIn(0L, 3L), now, note.clean(), now, id)
    }

    suspend fun reorderCriteria(orderedIds: List<String>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            orderedIds.forEachIndexed { i, id -> db.competenceCriterionQueries.reorder(i.toLong(), now, id) }
        }
    }

    suspend fun deleteCriterion(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            // Les liaisons qui visaient ce critère précis retombent sur la compétence entière.
            db.portfolioPieceCompetenceQueries.selectAll().executeAsList()
                .filter { it.criterion_id == id }
                .forEach { db.portfolioPieceCompetenceQueries.update(it.piece_id, it.competence_id, null, it.note, it.order_index, now, it.id) }
            db.competenceCriterionQueries.softDelete(now, now, id)
        }
    }

    private fun String?.clean(): String? = this?.trim()?.takeIf { it.isNotBlank() }
}
